import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import ArrowOutward from '@mui/icons-material/ArrowOutward';
import CloseRounded from '@mui/icons-material/CloseRounded';
import DarkModeRounded from '@mui/icons-material/DarkModeRounded';
import LightModeRounded from '@mui/icons-material/LightModeRounded';
import MenuRounded from '@mui/icons-material/MenuRounded';
import TouchAppRounded from '@mui/icons-material/TouchAppRounded';
import SyncRounded from '@mui/icons-material/SyncRounded';
import PlayCircleOutlineRounded from '@mui/icons-material/PlayCircleOutlineRounded';
import { SiInstagram, SiTiktok, SiX } from 'react-icons/si';
import typography from '../design-system/typography';
import { useAuth } from '../context/AuthContext';
import usePageTitle from '../hooks/usePageTitle';
import { getPlanCatalog } from '../services/subscriptionService';
import { REPORT_EXAMPLES_ENABLED } from '../services/reportExamplesService';
import './LandingPageModern.css';

const ReportExamples = REPORT_EXAMPLES_ENABLED
  ? lazy(() => import('../components/landing/ReportExamples'))
  : null;

const navigation = [
  ['how', 'como-funciona'],
  ['integration', 'productos'],
  ['coverage', 'cobertura'],
  ['example', 'ejemplo'],
  ['pricing', 'planes'],
  ['faq', 'faq'],
];

const integrationSteps = [
  ['web', TouchAppRounded],
  ['sync', SyncRounded],
  ['windows', PlayCircleOutlineRounded],
];

const socialProfiles = [
  { name: 'Instagram', handle: '@soccertycs', href: 'https://www.instagram.com/soccertycs/', icon: SiInstagram },
  { name: 'TikTok', handle: '@SoccerTycs', href: 'https://www.tiktok.com/@SoccerTycs', icon: SiTiktok },
  { name: 'X', handle: '@soccertycs', href: 'https://x.com/soccertycs', icon: SiX },
];

function PitchDiagram() {
  return (
    <div className="landing-pitch" aria-hidden="true">
      <svg viewBox="0 0 360 112">
        <rect x="30" y="12" width="300" height="88" rx="3" />
        <path d="M180 12v88M30 32h35v48H30m300-48h-35v48h35" />
        <circle cx="180" cy="56" r="21" />
        <path className="landing-pitch-route" d="M92 76q53-62 96-26t83-14" />
      </svg>
    </div>
  );
}

function SectionHeading({ eyebrow, title, description, id }) {
  return (
    <div className="landing-section-heading">
      <p className="landing-eyebrow">{eyebrow}</p>
      <h2 id={id}>{title}</h2>
      {description && <p className="landing-muted">{description}</p>}
    </div>
  );
}

export default function LandingPageModern({ themeMode = 'light', onToggleTheme = () => {} }) {
  const { t, i18n } = useTranslation('landing', { keyPrefix: 'commercial' });
  usePageTitle(t('pageTitle'));
  const { isAuthenticated, user } = useAuth();
  const { hash } = useLocation();
  const navigate = useNavigate();
  const pendingSection = useRef(null);
  const previousHash = useRef(hash);
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState(false);
  const [catalogAttempt, setCatalogAttempt] = useState(0);
  const [pricingScope, setPricingScope] = useState('individual');
  const [pricingRole, setPricingRole] = useState('coach');
  const [pricingPage, setPricingPage] = useState(0);
  const language = (i18n.resolvedLanguage || i18n.language || 'es').slice(0, 2);
  const accessPath = isAuthenticated ? '/dashboard' : '/login';
  const accessLabel = t(isAuthenticated ? 'nav.portal' : 'nav.access');
  const exampleLabel = t(ReportExamples ? 'hero.example' : 'example.structureAction');

  useEffect(() => {
    const target = hash.slice(1) || (previousHash.current ? 'landing-content' : '');
    previousHash.current = hash;
    const node = document.getElementById(target);
    if (!node) return undefined;
    const frame = requestAnimationFrame(() => {
      node.focus({ preventScroll: true });
      node.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(frame);
  }, [hash, reducedMotion]);

  useEffect(() => {
    let cancelled = false;
    setCatalogLoading(true);
    setCatalogError(false);
    getPlanCatalog()
      .then(data => { if (!cancelled) setCatalog(data); })
      .catch(() => { if (!cancelled) setCatalogError(true); })
      .finally(() => { if (!cancelled) setCatalogLoading(false); });
    return () => { cancelled = true; };
  }, [catalogAttempt]);

  const filteredPlans = useMemo(() => catalog.filter(plan => (
    plan.scope === pricingScope &&
    (pricingScope !== 'individual' || !plan.compatible_professional_roles?.length ||
      plan.compatible_professional_roles.includes(pricingRole))
  )), [catalog, pricingScope, pricingRole]);
  const totalPages = Math.max(1, Math.ceil(filteredPlans.length / 3));
  const plans = filteredPlans.slice(pricingPage * 3, pricingPage * 3 + 3);
  const workflow = t('workflow.items', { returnObjects: true });
  const questions = t('faq.items', { returnObjects: true });

  function scrollToSection(event, id) {
    if (event && (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
    const node = document.getElementById(id);
    if (!node) return;
    event?.preventDefault();
    if (mobileMenuOpen) pendingSection.current = node;
    setMobileMenuOpen(false);
    if (hash !== `#${id}`) {
      navigate({ hash: `#${id}` });
      return;
    }
    node.focus({ preventScroll: true });
    node.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
  }

  function sectionLink(key, id) {
    return <a key={id} href={`#${id}`} onClick={event => scrollToSection(event, id)}>{t(`nav.${key}`)}</a>;
  }

  return (
    <div
      className="landing-page"
      data-theme={themeMode}
      style={{
        '--landing-font': typography.fontFamily.primary,
      }}
    >
      <a className="landing-skip" href="#landing-content">{t('skip')}</a>
      <header className="landing-header">
        <div className="landing-container landing-header-inner">
          <RouterLink className="landing-brand" to="/" aria-label={t('nav.home')}>
            <img className="landing-brand-icon" src={`${process.env.PUBLIC_URL}/favicon.svg?v=20261027`} width="40" height="40" alt="" aria-hidden="true" />
            <span>Soccertycs</span>
          </RouterLink>
          <nav className="landing-desktop-nav" aria-label={t('nav.navigation')}>
            {navigation.map(([key, id]) => sectionLink(key, id))}
          </nav>
          <div className="landing-header-actions">
            <div className="landing-language" role="group" aria-label={t('nav.language')}>
              {['es', 'en'].map(lang => (
                <button type="button" key={lang} aria-label={lang === 'es' ? 'Español' : 'English'}
                  aria-pressed={language === lang} onClick={() => i18n.changeLanguage(lang)}>
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>
            <IconButton className="landing-icon-button" onClick={onToggleTheme}
              aria-label={t(themeMode === 'dark' ? 'nav.light' : 'nav.dark')}>
              {themeMode === 'dark' ? <LightModeRounded /> : <DarkModeRounded />}
            </IconButton>
            <RouterLink className="landing-button landing-button-primary landing-header-access" to={accessPath}>{accessLabel}</RouterLink>
            <IconButton className="landing-icon-button landing-menu-toggle" onClick={() => setMobileMenuOpen(true)}
              aria-label={t('nav.open')} aria-expanded={mobileMenuOpen} aria-controls={mobileMenuOpen ? 'landing-mobile-nav' : undefined}>
              <MenuRounded />
            </IconButton>
          </div>
        </div>
      </header>
      <Drawer anchor="right" open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)}
        ModalProps={{ disableRestoreFocus: Boolean(pendingSection.current) }}
        SlideProps={{ onExited: () => { pendingSection.current?.focus({ preventScroll: true }); pendingSection.current = null; } }}
        PaperProps={{ sx: { width: 'min(340px, 100vw)', p: 3 } }}>
        <div className="landing-drawer-heading">
          <strong>Soccertycs</strong>
          <IconButton aria-label={t('nav.close')} onClick={() => setMobileMenuOpen(false)}><CloseRounded /></IconButton>
        </div>
        <nav id="landing-mobile-nav" className="landing-mobile-nav" aria-label={t('nav.navigation')}>
          {navigation.map(([key, id]) => sectionLink(key, id))}
          <RouterLink to={accessPath} onClick={() => setMobileMenuOpen(false)}>{accessLabel}</RouterLink>
        </nav>
      </Drawer>

      <main id="landing-content" className="landing-container" tabIndex={-1}>
        <section className="landing-hero" aria-labelledby="landing-title">
          <div>
            <p className="landing-hero-chip">{t('hero.eyebrow')}</p>
            {isAuthenticated && user?.nombre && <p className="landing-welcome">{t('hero.welcome', { name: user.nombre })}</p>}
            <h1 id="landing-title">{t('hero.title')}<br /><span>{t('hero.accent')}</span></h1>
            <p className="landing-lead">{t('hero.description')}</p>
            <div className="landing-actions">
              <a className="landing-button landing-button-primary" href="#ejemplo" onClick={event => scrollToSection(event, 'ejemplo')}>
                {exampleLabel} <ArrowOutward fontSize="small" />
              </a>
              <a className="landing-button" href="#cobertura" onClick={event => scrollToSection(event, 'cobertura')}>{t('hero.coverage')}</a>
            </div>
            <p className="landing-caption">{t('hero.note')}</p>
            <div className="landing-tags">{t('hero.tags', { returnObjects: true }).map(tag => <span key={tag}>{tag}</span>)}</div>
            <a className="landing-text-link" href="#productos" onClick={event => scrollToSection(event, 'productos')}>
              {t('hero.integration')} <ArrowOutward fontSize="small" />
            </a>
          </div>
          <div className="landing-preview">
            <div className="landing-preview-top"><p className="landing-eyebrow">{t('preview.eyebrow')}</p><span className="landing-badge">{t('preview.badge')}</span></div>
            <h2>{t('preview.title')}<br /><span>{t('preview.accent')}</span></h2>
            <PitchDiagram />
            {t('preview.items', { returnObjects: true }).map((item, index) => (
              <div className="landing-report-row" key={item.title}>
                <span className="landing-number">{String(index + 1).padStart(2, '0')}</span>
                <div><strong>{item.title}</strong><p className="landing-muted">{item.description}</p></div>
              </div>
            ))}
            <p className="landing-caption">{t('preview.note')}</p>
          </div>
        </section>

        <div className="landing-audience">
          <span className="landing-eyebrow">{t('audience.title')}</span>
          {t('audience.segments', { returnObjects: true }).map(segment => <span key={segment}>{segment}</span>)}
          <span className="landing-caption">{t('audience.note')}</span>
        </div>

        <section className="landing-section" id="como-funciona" tabIndex={-1} aria-labelledby="landing-workflow-title">
          <SectionHeading id="landing-workflow-title" eyebrow={t('workflow.eyebrow')} title={t('workflow.title')} description={t('workflow.description')} />
          <div className="landing-grid-three">
            {workflow.map((item, index) => <article className="landing-card" key={item.title}>
              <span className="landing-step">{String(index + 1).padStart(2, '0')}</span>
              <h3>{item.title}</h3><p>{item.description}</p>
            </article>)}
          </div>
        </section>

        <section className="landing-section" id="cobertura" tabIndex={-1} aria-labelledby="landing-coverage-title">
          <SectionHeading id="landing-coverage-title" eyebrow={t('coverage.eyebrow')} title={t('coverage.title')} description={t('coverage.description')} />
          <p className="landing-notice">{t('coverage.notice')}</p>
          <div className="landing-coverage-grid">
            {t('coverage.items', { returnObjects: true }).map(item => <div className="landing-coverage-item" key={item}>
              <h3>{item}</h3><p className="landing-caption">{t('coverage.status')}</p>
            </div>)}
          </div>
          <div className="landing-actions">
            <RouterLink className="landing-button" to={accessPath}>{t('coverage.action')}</RouterLink>
          </div>
          <p className="landing-caption">{t('coverage.accessNote')}</p>
        </section>

        <section className="landing-example" id="ejemplo" tabIndex={-1} aria-labelledby="landing-example-title">
          {ReportExamples ? (
            <Suspense fallback={<p role="status">{t('samples.loading')}</p>}>
              <ReportExamples />
            </Suspense>
          ) : <>
          <div className="landing-section-heading">
            <p className="landing-eyebrow">{t('example.eyebrow')}</p>
            <h2 id="landing-example-title">{t('example.title')}</h2>
            <p>{t('example.description')}</p>
          </div>
          <span className="landing-badge">{t('example.badge')}</span>
          <p className="landing-caption">{t('example.note')}</p>
          <div className="landing-grid-three">
            {t('example.items', { returnObjects: true }).map((item, index) => <article className="landing-card" key={item.title}>
              <span className="landing-step">{String(index + 1).padStart(2, '0')}</span>
              <h3>{item.title}</h3><p>{item.description}</p>
            </article>)}
          </div>
          <details className="landing-details">
            <summary>{t('example.question')}</summary>
            <p>{t('example.answer')}</p>
          </details>
          <div className="landing-actions"><RouterLink className="landing-button landing-button-primary" to={accessPath}>{t('example.action')}</RouterLink></div>
          <p className="landing-caption">{t('example.permissions')}</p>
          </>}
        </section>

        <section className="landing-section" id="productos" tabIndex={-1} aria-labelledby="landing-products-title">
          <SectionHeading id="landing-products-title" eyebrow={t('products.eyebrow')} title={t('products.title')} description={t('products.description')} />
          <ol className="landing-grid-three landing-connection-steps">
            {integrationSteps.map(([key, Icon], index) => (
              <li className="landing-card landing-connection-step" key={key} id={key === 'windows' ? 'showcase' : undefined} tabIndex={key === 'windows' ? -1 : undefined}>
                <div className="landing-connection-meta">
                  <span className="landing-step" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
                  <Icon aria-hidden="true" />
                </div>
                <p className="landing-eyebrow">{t(`products.${key}.label`)}</p>
                <h3>{t(`products.${key}.title`)}</h3>
                <p>{t(`products.${key}.description`)}</p>
              </li>
            ))}
          </ol>
          <p className="landing-caption">{t('products.web.note')}</p>
          <p className="landing-caption">{t('products.windows.note')}</p>
          <div className="landing-actions">
            <RouterLink className="landing-button landing-button-primary" to={accessPath}>{accessLabel} <ArrowOutward fontSize="small" /></RouterLink>
            <RouterLink className="landing-button" to="/cliente-windows">{t('products.windows.action')} <ArrowOutward fontSize="small" /></RouterLink>
          </div>
        </section>

        <section className="landing-section" aria-labelledby="landing-purpose-title">
          <SectionHeading id="landing-purpose-title" eyebrow={t('purpose.eyebrow')} title={t('purpose.title')} />
          <p className="landing-purpose">{t('purpose.description')}</p>
          <p className="landing-caption">{t('purpose.note')}</p>
        </section>

        <section className="landing-section" id="planes" tabIndex={-1} aria-labelledby="landing-pricing-title">
          <SectionHeading id="landing-pricing-title" eyebrow={t('pricing.eyebrow')} title={t('pricing.title')} description={t('pricing.description')} />
          <div className="landing-pricing-selectors">
            <div className="landing-segmented" role="group" aria-label={t('pricing.scope')}>
              {['individual', 'club'].map(scope => <button type="button" key={scope} aria-pressed={pricingScope === scope}
                onClick={() => { setPricingScope(scope); setPricingPage(0); }}>{t(`pricing.${scope}`)}</button>)}
            </div>
            {pricingScope === 'individual' && <div className="landing-segmented" role="group" aria-label={t('pricing.role')}>
              {['coach', 'analyst'].map(role => <button type="button" key={role} aria-pressed={pricingRole === role}
                onClick={() => { setPricingRole(role); setPricingPage(0); }}>{t(`pricing.${role}`)}</button>)}
            </div>}
          </div>
          {catalogLoading ? <div className="landing-loading"><CircularProgress color="inherit" aria-label={t('pricing.loading')} /></div>
            : catalogError ? <div className="landing-notice" role="alert"><p>{t('pricing.error')}</p>
              <button type="button" className="landing-button" onClick={() => setCatalogAttempt(value => value + 1)}>{t('pricing.retry')}</button></div>
              : plans.length === 0 ? <p role="status" className="landing-notice">{t('pricing.empty')}</p>
                : <>
                  <div className="landing-grid-three">
                    {plans.map(plan => {
                      const price = plan.prices?.find(item => item.billing_interval === 'month');
                      const hasPrice = Number.isFinite(price?.unit_amount) && price.unit_amount >= 0;
                      const priceLabel = hasPrice ? new Intl.NumberFormat(language === 'en' ? 'en-GB' : 'es-ES', {
                        style: 'currency', currency: (price.currency || 'EUR').toUpperCase(),
                      }).format(price.unit_amount / 100) : '—';
                      return <article className="landing-card landing-plan" key={plan.plan_key} aria-label={plan.display_name}>
                        <h3>{plan.display_name}</h3><p className="landing-muted">{plan.description}</p>
                        <p className="landing-price">{priceLabel}{hasPrice && <span>{t('pricing.perMonth')}</span>}</p>
                        {plan.scope === 'club' && plan.included_staff_seats > 0 && <span className="landing-badge">{t('pricing.staff', { count: plan.included_staff_seats })}</span>}
                        {plan.scope === 'club' && plan.included_player_seats > 0 && <p className="landing-caption">{t('pricing.players', { count: plan.included_player_seats })}</p>}
                        <ul>{(plan.features || []).map(feature => <li key={feature}>{feature}</li>)}</ul>
                        {hasPrice ? <RouterLink className="landing-button landing-button-primary" to={accessPath}>{accessLabel}</RouterLink>
                          : <button type="button" className="landing-button" onClick={event => scrollToSection(event, 'faq')}>{t('pricing.conditions')}</button>}
                      </article>;
                    })}
                  </div>
                  {totalPages > 1 && <nav className="landing-pagination" aria-label={t('pricing.pagination')}>
                    {Array.from({ length: totalPages }, (_, index) => <button type="button" key={index}
                      aria-current={pricingPage === index ? 'page' : undefined} onClick={() => setPricingPage(index)}>{index + 1}</button>)}
                  </nav>}
                </>}
          <p className="landing-caption landing-pricing-note">{t('pricing.note')}</p>
        </section>

        <section className="landing-section" id="faq" tabIndex={-1} aria-labelledby="landing-faq-title">
          <SectionHeading id="landing-faq-title" eyebrow={t('faq.eyebrow')} title={t('faq.title')} />
          {questions.map((item, index) => <details className="landing-details" key={index}><summary>{item.question}</summary><p>{item.answer}</p></details>)}
        </section>
        <section className="landing-closing" aria-labelledby="landing-closing-title">
          <div><h2 id="landing-closing-title">{t('closing.title')}</h2><p>{t('closing.description')}</p></div>
          <a className="landing-button landing-button-primary" href="#ejemplo" onClick={event => scrollToSection(event, 'ejemplo')}>{exampleLabel} <ArrowOutward fontSize="small" /></a>
        </section>
      </main>

      <footer className="landing-footer">
        <div className="landing-container">
          <div><strong>Soccertycs</strong><p>{t('footer.description')}</p></div>
          <nav aria-label={t('footer.navigation')}>
            {sectionLink('coverage', 'cobertura')}
            {sectionLink('pricing', 'planes')}
            {sectionLink('faq', 'faq')}
            <RouterLink to="/cliente-windows">{t('products.windows.label')}</RouterLink>
            <RouterLink to={accessPath}>{accessLabel}</RouterLink>
          </nav>
          <nav className="landing-socials" aria-label={t('footer.socialNavigation')}>
            {socialProfiles.map(({ name, handle, href, icon: Icon }) => (
              <a key={name} href={href} target="_blank" rel="noopener noreferrer"
                aria-label={t('footer.socialProfile', { network: name, handle })}>
                <Icon size={20} aria-hidden="true" focusable="false" />
                <span>{name}</span>
                <ArrowOutward fontSize="small" aria-hidden="true" />
              </a>
            ))}
          </nav>
          <p className="landing-caption">{t('footer.copyright', { year: new Date().getFullYear() })}</p>
        </div>
      </footer>
    </div>
  );
}
